import Logger from '../Logger';
import hark from 'hark';
import { getSignalingUrl } from '../urlFactory';
import { SocketTimeoutError } from '../utils';
import * as meActions from '../../stores/actions/meActions';
import * as intlActions from '../../stores/actions/intlActions';
import * as roomActions from '../../stores/actions/roomActions';
import * as peerActions from '../../stores/actions/peerActions';
import * as alertActions from '../../stores/actions/alertActions';
import * as peerVolumeActions from '../../stores/actions/peerVolumeActions';
import * as settingsActions from '../../stores/actions/settingsActions';
import * as closedCaptionActions from '../../stores/actions/closedCaptionActions';
import * as lobbyPeerActions from '../../stores/actions/lobbyPeerActions';
import * as fileActions from '../../stores/actions/fileActions';
import * as consumerActions from '../../stores/actions/consumerActions';
import * as producerActions from '../../stores/actions/producerActions';
import { toast } from 'react-toastify';
import Spotlights from './Spotlights';
import { permissions } from '../permissions';
import * as locales from '../translations/locales';
import { createIntl } from 'react-intl';
import { getDevicesByType } from '../deviceInfo';
import { VIDEO_CONSTRAINS, userRoles } from '../constants';
import { stopRecording } from '../../api/general/recordingBot';
import localData from '../localStorageService';

let mediasoupClient;

let io;

let ScreenShare;

let requestTimeout;

if (process.env.NODE_ENV !== 'test')
{
	({
		requestTimeout = 20000
	} = window.config);
}

const logger = new Logger('RoomClient');

const PC_PROPRIETARY_CONSTRAINTS = {
	optional: [ { googDscp: true } ]
};

const VIDEO_SIMULCAST_ENCODINGS = [
	{ scaleResolutionDownBy: 4, maxBitRate: 100000 },
	{ scaleResolutionDownBy: 1, maxBitRate: 1200000 }
];

// Used for VP9 webcam video.
const VIDEO_KSVC_ENCODINGS = [ { scalabilityMode: 'S3T3_KEY' } ];

// Used for VP9 desktop sharing.
const VIDEO_SVC_ENCODINGS = [ { scalabilityMode: 'S3T3', dtx: true } ];

let store;

let intl;

export default class RoomClient
{
	/**
   * @param  {Object} data
   * @param  {Object} data.store - The Redux store.
   * @param  {Object} data.intl - react-intl object
   */
	static init(data)
	{
		store = data.store;
	}

	constructor(device)
	{
		this.audioInProgress = false;
		this.signalingUrl = null;

		// Closed flag.
		this.closed = false;

		// Whether simulcast should be used.
		this.useSimulcast = false;

		if ('simulcast' in window.config)
			this.useSimulcast = window.config.simulcast;

		// Whether simulcast should be used for sharing
		this.useSharingSimulcast = false;

		if ('simulcastSharing' in window.config)
			this.useSharingSimulcast = window.config.simulcastSharing;

		// mediasoup-client Device instance.
		// @type {mediasoupClient.Device}
		this.mediasoupDevice = null;

		// Put the browser info into state
		store.dispatch(meActions.setBrowser(device));

		// Manager of spotlight
		this.spotlights = new Spotlights(this._maxSpotlights, false, this);

		// Transport for sending.
		this.sendTransport = null;

		// Transport for receiving.
		this.recvTransport = null;

		// Local mic mediasoup Producer.
		this.micProducer = null;

		// This device
		this.device = device;

		// Local mic hark
		this.hark = null;

		// Local MediaStream for hark
		this.harkStream = null;

		// Local webcam mediasoup Producer.
		this.webcamProducer = null;

		// Map of webcam MediaDeviceInfos indexed by deviceId.
		// @type {Map<String, MediaDeviceInfos>}
		this.webcams = {};

		this.audioInputDevices = {};

		this.audioOutputDevices = {};

		// mediasoup Consumers.
		// @type {Map<String, mediasoupClient.Consumer>}
		this.consumers = new Map();

		this.screenSharing = null;

		this.screenSharingProducer = null;

		this.peerId = null;

		this.userId = null;

		this.host = false;

		this.admin = false;

		this._stream = null;

		this.setLocale(store.getState().intl.locale);
	}

	close(kicked)
	{
		store.dispatch(meActions.setMeInitialize());
		store.dispatch(settingsActions.toggleClosedCaption(false));
		logger.debug('close()');

		this._stream && this._stream.getTracks()
			.map((track) => track.stop());
		this.closed = true;
		this.signalingSocket?.close();

		// Close mediasoup Transports.
		if (this.sendTransport) this.sendTransport.close();

		if (this.recvTransport) this.recvTransport.close();

		this.spotlights.clearSpotlights();
		store.dispatch(producerActions.clearProducers());
		store.dispatch(peerActions.clearPeers());
		store.dispatch(consumerActions.clearConsumers());
		store.dispatch(roomActions.clearSpotlights());

		if (kicked)
		{
			store.dispatch(roomActions.setRoomState('kicked'));
		}
		else
		{
			store.dispatch(roomActions.setRoomState('closed'));
		}
	}

	startDevicesListener()
	{
		navigator.mediaDevices.addEventListener('devicechange', async () =>
		{
			logger.debug(
				'startDevicesListener() | navigator.mediaDevices.ondevicechange'
			);

			await this.updateAudioDevices();
			await this.updateWebcams();
			await this.updateAudioOutputDevices();
		});
	}

	setLocale(locale)
	{
		if (locale === null) locale = locales.detect();

		const one = locales.loadOne(locale);

		store.dispatch(
			intlActions.updateIntl({
				locale: one.locale[0],
				messages: one.messages,
				list: locales.getList()
			})
		);

		intl = createIntl({
			locale: store.getState().intl.locale,
			messages: store.getState().intl.messages
		});

		document.documentElement.lang = store.getState().intl.locale.toUpperCase();
	}

	soundNotification()
	{
		const { notificationSounds } = store.getState().settings;

		if (notificationSounds)
		{
			const alertPromise = this.soundAlert.play();

			if (alertPromise !== undefined)
			{
				alertPromise.then().catch((error) =>
				{
					logger.error('soundAlert.play() [error:"%o"]', error);
				});
			}
		}
	}

	timeoutCallback(callback)
	{
		let called = false;

		const interval = setTimeout(() =>
		{
			if (called) return;
			called = true;
			callback(new SocketTimeoutError('Request timed out'));
		}, requestTimeout);

		return (...args) =>
		{
			if (called) return;
			called = true;
			clearTimeout(interval);

			callback(...args);
		};
	}

	_sendRequest(method, data)
	{
		return new Promise((resolve, reject) =>
		{
			if (!this.signalingSocket)
			{
				reject('No socket connection');
			}
			else
			{
				this.signalingSocket.emit(
					'request',
					{ method, data },
					this.timeoutCallback((err, response) =>
					{
						if (err) reject(err);
						else resolve(response);
					})
				);
			}
		});
	}

	async sendRequest(method, data)
	{
		logger.debug('sendRequest() [method:"%s", data:"%o"]', method, data);

		const { requestRetries = 3 } = window.config;

		for (let tries = 0; tries < requestRetries; tries++)
		{
			try
			{
				return await this._sendRequest(method, data);
			}
			catch (error)
			{
				if (error instanceof SocketTimeoutError && tries < requestRetries)
					logger.warn(
						'sendRequest() | timeout, retrying [attempt:"%s"]',
						tries
					);
				else throw error;
			}
		}
	}

	async sendClosedCaption(sttId, sessionId, offset, isFinal, text)
	{
		try
		{
			store.dispatch(closedCaptionActions.addClosedCaption(sttId, sessionId, offset, isFinal, text, this.peerId));

			await this.sendRequest('closedCaption', { sttId, sessionId, offset, isFinal, text, peerId: this.peerId });
		}
		catch (error)
		{
			logger.error('sendClosedCaption() [error:"%o"]', error);

			toast(intl.formatMessage({
				id: 'room.closedCaptionError',
				defaultMessage: 'Unable to send closedCaption'
			})
			);
		}
	}

	async activeClosedCaption(closedCaption)
	{
		logger.debug('activeClosedCaption() [closedCaption:"%s"]', closedCaption);

		try
		{
			store.dispatch(settingsActions.toggleClosedCaption(closedCaption));

			if (!closedCaption)
			{
				store.dispatch(closedCaptionActions.clearClosedCaption());
			}
		}
		catch (error)
		{
			logger.error('activeClosedCaption() [error:"%o"]', error);

			toast(intl.formatMessage({
				id: 'closedCaptionActiveError',
				defaultMessage: 'Unable to active closedCaption'
			})
			);
		}
	}

	async shareFiles(roomFile)
	{
		store.dispatch(fileActions.addFile(roomFile));

		this.sendFile(roomFile);
	}

	async deleteFile(id)
	{
		store.dispatch(fileActions.deleteFile(id));

		this._deleteFile(id);
	}

	// { file, name, picture }
	async sendFile(roomFile)
	{
		logger.debug('sendFile() [filepath:"%o"]', roomFile);

		try
		{
			await this.sendRequest('sendFile', { roomFile });
		}
		catch (error)
		{
			logger.error('sendFile() [error:"%o"]', error);

			toast(intl.formatMessage({
				id: 'filesharing.unableToShare',
				defaultMessage: 'Unable to share file'
			})
			);
		}
	}

	async _deleteFile(id)
	{
		logger.debug('deleteFile() [id of file:"%o"]', id);

		try
		{
			await this.sendRequest('deleteFile', { id });
		}
		catch (error)
		{
			logger.error('deleteFile() [error:"%o"]', error);

			toast(intl.formatMessage({
				id: 'filesharing.unableToDelete',
				defaultMessage: 'Unable to delete file'
			})
			);
		}
	}

	async muteMic()
	{
		if (this.audioInProgress) return;

		this.audioInProgress = true;
		logger.debug('muteMic()');
		store.dispatch(meActions.setAudioInProgress(true));
		if (this.micProducer) this.micProducer.pause();

		try
		{
			await this.sendRequest('pauseProducer', {
				producerId: this.micProducer.id
			});

			store.dispatch(producerActions.setProducerPaused(this.micProducer.id));
			localData.set('mic.muted', true);

			this.audioInProgress = false;
			store.dispatch(settingsActions.setAudioMuted(true));
			store.dispatch(meActions.setAudioInProgress(false));
		}
		catch (error)
		{
			logger.error('muteMic() [error:"%o"]', error);
			this.audioInProgress = false;
			store.dispatch(meActions.setAudioInProgress(false));
			toast(intl.formatMessage({
				id: 'devices.microphoneMuteError',
				defaultMessage: 'Unable to mute your microphone'
			})
			);
		}
	}

	async unmuteMic()
	{
		logger.debug('unmuteMic()');
		if (this.audioInProgress) return;

		this.audioInProgress = true;
		store.dispatch(meActions.setAudioInProgress(true));
		if (!this.micProducer)
		{
			await this.updateMic();
			localData.set('mic.muted', false);
		}
		else
		{
			this.micProducer.resume();
			localData.set('mic.muted', false);

			try
			{
				await this.sendRequest('resumeProducer', {
					producerId: this.micProducer.id
				});

				store.dispatch(
					producerActions.setProducerResumed(this.micProducer.id)
				);

				store.dispatch(settingsActions.setAudioMuted(false));
			}
			catch (error)
			{
				logger.error('unmuteMic() [error:"%o"]', error);

				toast(intl.formatMessage({
					id: 'devices.microphoneUnMuteError',
					defaultMessage: 'Unable to unmute your microphone'
				})
				);
			}
		}

		this.audioInProgress = false;
		store.dispatch(meActions.setAudioInProgress(false));
	}

	changeMaxSpotlights(maxSpotlights)
	{
		this.spotlights.maxSpotlights = maxSpotlights;

		store.dispatch(settingsActions.setLastN(maxSpotlights));
	}

	// Updated consumers based on spotlights
	async updateSpotlights(spotlights)
	{
		logger.debug('updateSpotlights()');
		store.dispatch(roomActions.setSpotlights(spotlights));

		try
		{
			for (const consumer of this.consumers.values())
			{
				if (consumer.kind === 'video')
				{
					if (spotlights.includes(consumer.appData.peerId))
						await this.resumeConsumer(consumer);
					else
					{
						await this.pauseConsumer(consumer);
						store.dispatch(
							roomActions.removeSelectedPeer(consumer.appData.peerId)
						);
					}
				}
			}
		}
		catch (error)
		{
			logger.error('updateSpotlights() [error:"%o"]', error);
		}
	}

	disconnectLocalHark()
	{
		logger.debug('disconnectLocalHark()');

		if (this.harkStream != null)
		{
			let [ track ] = this.harkStream.getAudioTracks();

			track.stop();
			track = null;

			this.harkStream = null;
		}

		if (this.hark != null) this.hark.stop();
	}

	connectLocalHark(track)
	{
		logger.debug('connectLocalHark() [track:"%o"]', track);

		this.harkStream = new MediaStream();

		const newTrack = track.clone();

		this.harkStream.addTrack(newTrack);

		newTrack.enabled = true;

		this.hark = hark(this.harkStream, {
			play: false,
			interval: 10,
			threshold: store.getState().settings.noiseThreshold,
			history: 100
		});

		this.hark.on('speaking', () =>
		{
			store.dispatch(meActions.setIsSpeaking(true));

			if (
				(store.getState().settings.voiceActivatedUnmute ||
          store.getState().me.isAutoMuted) &&
        this.micProducer &&
        this.micProducer.paused
			)
				this.micProducer.resume();

			store.dispatch(meActions.setAutoMuted(false)); // sanity action
		});

		this.hark.on('stopped_speaking', () =>
		{
			store.dispatch(meActions.setIsSpeaking(false));

			if (
				store.getState().settings.voiceActivatedUnmute &&
        this.micProducer &&
        !this.micProducer.paused
			)
			{
				this.micProducer.pause();

				store.dispatch(meActions.setAutoMuted(true));
			}
		});
	}

	async changeAudioOutputDevice(deviceId)
	{
		logger.debug('changeAudioOutputDevice() [deviceId:"%s"]', deviceId);

		store.dispatch(meActions.setAudioOutputInProgress(true));

		try
		{
			const device = this.audioOutputDevices[deviceId];

			if (!device)
				throw new Error('Selected audio output device no longer available');

			store.dispatch(settingsActions.setSelectedAudioOutputDevice(deviceId));

			await this.updateAudioOutputDevices();
		}
		catch (error)
		{
			logger.error('changeAudioOutputDevice() [error:"%o"]', error);
		}

		store.dispatch(meActions.setAudioOutputInProgress(false));
	}

	// Only Firefox supports applyConstraints to audio tracks
	// See:
	// https://bugs.chromium.org/p/chromium/issues/detail?id=796964
	async updateMic({ newDeviceId = null } = {})
	{
		if (this.audioInProgress) return;

		store.dispatch(meActions.setAudioInProgress(true));
		logger.debug(
			'updateMic() [start:"%s", restart:"%s", newDeviceId:"%s"]',
			newDeviceId
		);

		let track;

		try
		{
			if (!this.mediasoupDevice.canProduce('audio'))
				throw new Error('cannot produce audio');

			if (newDeviceId)
				store.dispatch(settingsActions.setSelectedAudioDevice(newDeviceId));

			this.audioInProgress = true;

			const deviceId = await this.getAudioDeviceId();
			const device = this.audioInputDevices[deviceId];

			if (!device)
			{
				this.audioInProgress = false;

				throw new Error('no audio devices');
			}

			const { autoGainControl, echoCancellation, noiseSuppression } =
        store.getState().settings;

			if (!window.config.centralAudioOptions)
			{
				this.audioInProgress = false;
				throw new Error(
					'Missing centralAudioOptions from app config! (See it in example config.)'
				);
			}

			const {
				sampleRate = 48000,
				channelCount = 1,
				volume = 1.0,
				sampleSize = 16,
				opusStereo = false,
				opusDtx = true,
				opusFec = true,
				opusPtime = 20,
				opusMaxPlaybackRate = 48000
			} = window.config.centralAudioOptions;

			this.disconnectLocalHark();

			if (this.micProducer) await this.disableMic();

			this._stream && this._stream.getTracks().map((track) => track.stop());

			this._stream = await navigator.mediaDevices.getUserMedia({
				audio: {
					deviceId: { ideal: deviceId },
					sampleRate,
					channelCount,
					volume,
					autoGainControl,
					echoCancellation,
					noiseSuppression,
					sampleSize
				}
			});

			[ track ] = this._stream.getAudioTracks();

			const { deviceId: trackDeviceId } = track.getSettings();

			store.dispatch(settingsActions.setSelectedAudioDevice(trackDeviceId));

			this.micProducer = await this.sendTransport.produce({
				track,
				codecOptions: {
					opusStereo,
					opusDtx,
					opusFec,
					opusPtime,
					opusMaxPlaybackRate
				},
				appData: { source: 'mic' }
			});
			localData.set('mic.muted', false);

			store.dispatch(
				producerActions.addProducer({
					id: this.micProducer.id,
					source: 'mic',
					paused: this.micProducer.paused,
					track: this.micProducer.track,
					rtpParameters: this.micProducer.rtpParameters,
					codec:
						this.micProducer.rtpParameters.codecs[0].mimeType.split('/')[1]
				})
			);

			this.micProducer.on('transportclose', () =>
			{
				this.micProducer = null;
			});

			this.micProducer.on('trackended', () =>
			{
				toast(intl.formatMessage({
					id: 'devices.microphoneDisconnected',
					defaultMessage: 'Microphone disconnected'
				})
				);

				this.disableMic();
			});

			this.micProducer.volume = 0;

			this.connectLocalHark(track);

			await this.updateAudioDevices();
		}
		catch (error)
		{
			logger.error('updateMic() [error:"%o"]', error);

			toast(intl.formatMessage({
				id: 'devices.microphoneError',
				defaultMessage: 'Microphone access was disabled'
			})
			);

			if (track) track.stop();
		}

		this.audioInProgress = false;

		store.dispatch(meActions.setAudioInProgress(false));
	}

	async updateWebcam({
		init = false,
		start = false,
		restart = false,
		newDeviceId = null,
		newResolution = null,
		newFrameRate = null
	} = {})
	{
		logger.debug(
			'updateWebcam() [start:"%s", restart:"%s", newDeviceId:"%s", newResolution:"%s", newFrameRate:"%s"]',
			start,
			restart,
			newDeviceId,
			newResolution,
			newFrameRate
		);

		let track;

		try
		{
			if (!this.mediasoupDevice.canProduce('video'))
				throw new Error('cannot produce video');

			if (newDeviceId && !restart)
				throw new Error('changing device requires restart');

			if (newDeviceId)
				store.dispatch(settingsActions.setSelectedWebcamDevice(newDeviceId));

			if (newResolution)
				store.dispatch(settingsActions.setVideoResolution(newResolution));

			if (newFrameRate)
				store.dispatch(settingsActions.setVideoFrameRate(newFrameRate));

			const { videoMuted } = store.getState().settings;

			if (init && videoMuted) return;
			else store.dispatch(settingsActions.setVideoMuted(false));

			store.dispatch(meActions.setWebcamInProgress(true));

			const deviceId = await this.getWebcamDeviceId();
			const device = this.webcams[deviceId];

			if (!device) throw new Error('no webcam devices');

			const { resolution, frameRate } = store.getState().settings;

			if ((restart && this.webcamProducer) || start)
			{

				if (this.webcamProducer) await this.disableWebcam();

				const stream = await navigator.mediaDevices.getUserMedia({
					video: {
						deviceId: { ideal: deviceId },
						...VIDEO_CONSTRAINS[resolution],
						frameRate
					}
				});

				[ track ] = stream.getVideoTracks();

				const { deviceId: trackDeviceId } = track.getSettings();

				store.dispatch(settingsActions.setSelectedWebcamDevice(trackDeviceId));

				if (this.useSimulcast)
				{
					// If VP9 is the only available video codec then use SVC.
					const firstVideoCodec =
            this.mediasoupDevice.rtpCapabilities.codecs.find(
            	(c) => c.kind === 'video'
            );

					let encodings;

					if (firstVideoCodec.mimeType.toLowerCase() === 'video/vp9')
						encodings = VIDEO_KSVC_ENCODINGS;
					else if ('simulcastEncodings' in window.config)
						encodings = window.config.simulcastEncodings;
					else encodings = VIDEO_SIMULCAST_ENCODINGS;

					this.webcamProducer = await this.sendTransport.produce({
						track,
						encodings,
						codecOptions: {
							videoGoogleStartBitrate: 1000
						},
						appData: {
							source: 'webcam'
						}
					});
				}
				else
				{
					this.webcamProducer = await this.sendTransport.produce({
						track,
						appData: {
							source: 'webcam'
						}
					});
				}

				store.dispatch(
					producerActions.addProducer({
						id: this.webcamProducer.id,
						source: 'webcam',
						paused: this.webcamProducer.paused,
						track: this.webcamProducer.track,
						rtpParameters: this.webcamProducer.rtpParameters,
						codec:
              this.webcamProducer.rtpParameters.codecs[0].mimeType.split(
              	'/'
              )[1]
					})
				);

				this.webcamProducer.on('transportclose', () =>
				{
					this.webcamProducer = null;
				});

				this.webcamProducer.on('trackended', () =>
				{
					toast(intl.formatMessage({
						id: 'devices.cameraDisconnected',
						defaultMessage: 'Camera disconnected'
					})
					);

					this.disableWebcam();
				});
			}
			else if (this.webcamProducer)
			{
				({ track } = this.webcamProducer);

				await track.applyConstraints({
					...VIDEO_CONSTRAINS[resolution],
					frameRate
				});

			}

			await this.updateWebcams();
		}
		catch (error)
		{
			logger.error('updateWebcam() [error:"%o"]', error);

			toast(intl.formatMessage({
				id: 'devices.cameraError',
				defaultMessage: 'Camera access was disabled'
			})
			);

			if (track) track.stop();
		}

		store.dispatch(meActions.setWebcamInProgress(false));
	}

	addSelectedPeer(peerId)
	{
		logger.debug('addSelectedPeer() [peerId:"%s"]', peerId);

		this.spotlights.addPeerToSelectedSpotlights(peerId);

		store.dispatch(roomActions.addSelectedPeer(peerId));
	}

	setSelectedPeer(peerId)
	{
		logger.debug('setSelectedPeer() [peerId:"%s"]', peerId);

		this.clearSelectedPeers();
		this.addSelectedPeer(peerId);
	}

	clearSelectedPeers()
	{
		logger.debug('clearSelectedPeers()');

		this.spotlights.clearPeersFromSelectedSpotlights();

		store.dispatch(
			roomActions.clearSelectedPeers());
	}

	removeSelectedPeer(peerId)
	{
		logger.debug('removeSelectedPeer() [peerId:"%s"]', peerId);

		this.spotlights.removePeerSpotlight(peerId);

		store.dispatch(roomActions.removeSelectedPeer(peerId));
	}

	async promoteAllLobbyPeers()
	{
		logger.debug('promoteAllLobbyPeers()');

		store.dispatch(roomActions.setLobbyPeersPromotionInProgress(true));

		try
		{
			await this.sendRequest('promoteAllPeers');
		}
		catch (error)
		{
			logger.error('promoteAllLobbyPeers() [error:"%o"]', error);
		}

		store.dispatch(roomActions.setLobbyPeersPromotionInProgress(false));
	}

	async promoteLobbyPeer(peerId)
	{
		logger.debug('promoteLobbyPeer() [peerId:"%s"]', peerId);

		store.dispatch(
			lobbyPeerActions.setLobbyPeerPromotionInProgress(peerId, true)
		);

		try
		{
			await this.sendRequest('promotePeer', { peerId });
		}
		catch (error)
		{
			logger.error('promoteLobbyPeer() [error:"%o"]', error);
		}

		store.dispatch(
			lobbyPeerActions.setLobbyPeerPromotionInProgress(peerId, false)
		);
	}

	async givePeerRole(peerId, roleId)
	{
		logger.debug('givePeerRole() [peerId:"%s", roleId:"%s"]', peerId, roleId);
		const peers = Object.values(store.getState().peers);
		const hosts = peers.filter((peer) => Boolean(peer.roles.some((peerRole) => peerRole === userRoles.MODERATOR)) &&
			Boolean(peer.roles.some((peerRole) => peerRole !== userRoles.ADMIN)));

		if (hosts.length > 1)
		{
			const title = intl.formatMessage({ id: 'role.update.failed', defaultMessage: 'Role update failed' });
			const description = intl.formatMessage({ id: 'role.update.description', defaultMessage: 'There are one more hosts in the meeting room.' });
			const okTitle = intl.formatMessage({ id: 'label.ok', defaultMessage: 'OK' });

			store.dispatch(alertActions.setAlert({ title, description, okTitle, type: 'error' }));

			return;
		}

		store.dispatch(peerActions.setPeerModifyRolesInProgress(peerId, true));

		try
		{
			await this.sendRequest('moderator:giveRole', { peerId, roleId });
		}
		catch (error)
		{
			logger.error('givePeerRole() [error:"%o"]', error);
		}

		store.dispatch(peerActions.setPeerModifyRolesInProgress(peerId, false));
	}

	async removePeerRole(peerId, roleId)
	{
		logger.debug('removePeerRole() [peerId:"%s", roleId:"%s"]', peerId, roleId);

		store.dispatch(peerActions.setPeerModifyRolesInProgress(peerId, true));

		try
		{
			await this.sendRequest('moderator:removeRole', { peerId, roleId });
		}
		catch (error)
		{
			logger.error('removePeerRole() [error:"%o"]', error);
		}

		store.dispatch(peerActions.setPeerModifyRolesInProgress(peerId, false));
	}

	async kickPeer(peerId)
	{
		logger.debug('kickPeer() [peerId:"%s"]', peerId);

		store.dispatch(peerActions.setPeerKickInProgress(peerId, true));

		try
		{
			await this.sendRequest('moderator:kickPeer', { peerId });
		}
		catch (error)
		{
			logger.error('kickPeer() [error:"%o"]', error);
		}

		store.dispatch(peerActions.setPeerKickInProgress(peerId, false));
	}

	async mutePeer(peerId)
	{
		logger.debug('mutePeer() [peerId:"%s"]', peerId);

		store.dispatch(peerActions.setMutePeerInProgress(peerId, true));

		try
		{
			await this.sendRequest('moderator:mute', { peerId });
		}
		catch (error)
		{
			logger.error('mutePeer() [error:"%o"]', error);
		}

		store.dispatch(peerActions.setMutePeerInProgress(peerId, false));
	}

	async unmutePeer(peerId)
	{
		logger.debug('unmutePeer() [peerId:"%s"]', peerId);

		store.dispatch(peerActions.setMutePeerInProgress(peerId, true));

		try
		{
			await this.sendRequest('moderator:unmute', { peerId });
		}
		catch (error)
		{
			logger.error('unmutePeer() [error:"%o"]', error);
		}

		store.dispatch(peerActions.setMutePeerInProgress(peerId, false));
	}

	async stopPeerVideo(peerId)
	{
		logger.debug('stopPeerVideo() [peerId:"%s"]', peerId);

		store.dispatch(peerActions.setStopPeerVideoInProgress(peerId, true));

		try
		{
			await this.sendRequest('moderator:stopVideo', { peerId });
		}
		catch (error)
		{
			logger.error('stopPeerVideo() [error:"%o"]', error);
		}

		store.dispatch(peerActions.setStopPeerVideoInProgress(peerId, false));
	}

	async muteAllPeers()
	{
		logger.debug('muteAllPeers()');

		store.dispatch(roomActions.setMuteAllInProgress(true));

		try
		{
			await this.sendRequest('moderator:muteAll');
			store.dispatch(roomActions.setMutedAll(true));
		}
		catch (error)
		{
			logger.error('muteAllPeers() [error:"%o"]', error);
		}

		store.dispatch(roomActions.setMuteAllInProgress(false));
	}

	async unmuteAllPeers()
	{
		logger.debug('unmuteAllPeers()');

		store.dispatch(roomActions.setMuteAllInProgress(true));

		try
		{
			await this.sendRequest('moderator:unmuteAll');
			store.dispatch(roomActions.setMutedAll(false));
		}
		catch (error)
		{
			logger.error('unmuteAllPeers() [error:"%o"]', error);
		}

		store.dispatch(roomActions.setMuteAllInProgress(false));
	}

	async stopAllPeerVideo()
	{
		logger.debug('stopAllPeerVideo()');

		store.dispatch(roomActions.setStopAllVideoInProgress(true));

		try
		{
			await this.sendRequest('moderator:stopAllVideo');
		}
		catch (error)
		{
			logger.error('stopAllPeerVideo() [error:"%o"]', error);
		}

		store.dispatch(roomActions.setStopAllVideoInProgress(false));
	}

	async stopAllPeerScreenSharing()
	{
		logger.debug('stopAllPeerScreenSharing()');

		store.dispatch(roomActions.setStopAllScreenSharingInProgress(true));

		try
		{
			await this.sendRequest('moderator:stopAllScreenSharing');
		}
		catch (error)
		{
			logger.error('stopAllPeerScreenSharing() [error:"%o"]', error);
		}

		store.dispatch(roomActions.setStopAllScreenSharingInProgress(false));
	}

	async closeMeeting()
	{
		logger.debug('closeMeeting()');

		store.dispatch(roomActions.setCloseMeetingInProgress(true));

		try
		{
			await this.sendRequest('moderator:closeMeeting');
		}
		catch (error)
		{
			logger.error('closeMeeting() [error:"%o"]', error);
		}

		store.dispatch(roomActions.setCloseMeetingInProgress(false));
	}

	// type: mic/webcam/screen
	// mute: true/false
	async modifyPeerConsumer(peerId, type, mute)
	{
		logger.debug('modifyPeerConsumer() [peerId:"%s", type:"%s"]', peerId, type);

		if (type === 'mic')
			store.dispatch(peerActions.setPeerAudioInProgress(peerId, true));
		else if (type === 'webcam')
			store.dispatch(peerActions.setPeerVideoInProgress(peerId, true));
		else if (type === 'screen')
			store.dispatch(peerActions.setPeerScreenInProgress(peerId, true));

		try
		{
			for (const consumer of this.consumers.values())
			{
				if (
					consumer.appData.peerId === peerId &&
          consumer.appData.source === type
				)
				{
					if (mute) await this.pauseConsumer(consumer);
					else await this.resumeConsumer(consumer);
				}
			}
		}
		catch (error)
		{
			logger.error('modifyPeerConsumer() [error:"%o"]', error);
		}

		if (type === 'mic')
			store.dispatch(peerActions.setPeerAudioInProgress(peerId, false));
		else if (type === 'webcam')
			store.dispatch(peerActions.setPeerVideoInProgress(peerId, false));
		else if (type === 'screen')
			store.dispatch(peerActions.setPeerScreenInProgress(peerId, false));
	}

	async pauseConsumer(consumer)
	{
		logger.debug('pauseConsumer() [consumer:"%o"]', consumer);

		if (consumer.paused || consumer.closed) return;

		try
		{
			await this.sendRequest('pauseConsumer', { consumerId: consumer.id });

			consumer.pause();

			store.dispatch(consumerActions.setConsumerPaused(consumer.id, 'local'));
		}
		catch (error)
		{
			logger.error('pauseConsumer() [error:"%o"]', error);
		}
	}

	async resumeConsumer(consumer)
	{
		logger.debug('resumeConsumer() [consumer:"%o"]', consumer);

		if (!consumer.paused || consumer.closed) return;

		try
		{
			await this.sendRequest('resumeConsumer', { consumerId: consumer.id });

			consumer.resume();

			store.dispatch(consumerActions.setConsumerResumed(consumer.id, 'local'));
		}
		catch (error)
		{
			logger.error('resumeConsumer() [error:"%o"]', error);
		}
	}

	async loadDynamicImports()
	{
		({ default: ScreenShare } = await import(

			/* webpackPrefetch: true */
			/* webpackChunkName: "screensharing" */
			'./ScreenShare'
		));

		mediasoupClient = await import(

			/* webpackPrefetch: true */
			/* webpackChunkName: "mediasoup" */
			'mediasoup-client'
		);

		({ default: io } = await import(

			/* webpackPrefetch: true */
			/* webpackChunkName: "socket.io" */
			'socket.io-client'
		));
	}

	reset()
	{
		if (this.screenSharingProducer)
		{
			this.screenSharingProducer.close();

			store.dispatch(
				producerActions.removeProducer(this.screenSharingProducer.id)
			);

			this.screenSharingProducer = null;
		}

		if (this.webcamProducer)
		{
			this.webcamProducer.close();

			store.dispatch(producerActions.removeProducer(this.webcamProducer.id));

			this.webcamProducer = null;
		}

		if (this.micProducer)
		{
			this.micProducer.close();

			store.dispatch(producerActions.removeProducer(this.micProducer.id));

			this.micProducer = null;
		}

		if (this.sendTransport)
		{
			this.sendTransport.close();

			this.sendTransport = null;
		}

		if (this.recvTransport)
		{
			this.recvTransport.close();

			this.recvTransport = null;
		}

		this.spotlights.clearSpotlights();

		store.dispatch(peerActions.clearPeers());
		store.dispatch(consumerActions.clearConsumers());
		store.dispatch(roomActions.clearSpotlights());
		store.dispatch(roomActions.setRoomState('connecting'));
		store.dispatch(meActions.setMeInitialize());
	}

	async join({ peerId, userId, type, displayName, roomId, joinVideo, joinAudio, host, admin })
	{
		// await navigator.mediaDevices.getUserMedia({ audio: true });
		this.reset();
		await this.loadDynamicImports();

		const user = store.getState().auth.user;

		store.dispatch(meActions.setMe({ peerId }));
		store.dispatch(meActions.setPicture(user?.photo));
		store.dispatch(meActions.setMeUserId(user?.id));

		this.peerId = peerId;
		this.roomId = roomId;
		this.host = host;
		this.admin = admin;

		store.dispatch(roomActions.setRoomName(roomId));

		this.signalingUrl = getSignalingUrl(this.peerId, this.roomId, this.host, this.admin);

		this.screenSharing = ScreenShare.create(this.device);

		this.signalingSocket = io(this.signalingUrl,
			{
				transports: ['websocket'],
				reconnection: false,
				forceNew: true
			}
		);

		store.dispatch(roomActions.setRoomState('connecting'));

		this.signalingSocket.on('connect', async () =>
		{
			logger.debug('signaling Peer "connect" event');
			this.closed = false;
			store.dispatch(alertActions.closeAlert());
		});

		this.signalingSocket.on('disconnect', (reason) =>
		{
			logger.warn('signaling Peer "disconnect" event [reason:"%s"]', reason);

			if (this.closed) return;

			if (reason === 'io server disconnect')
			{
				toast(intl.formatMessage({
					id: 'socket.disconnected',
					defaultMessage: 'You are disconnected'
				})
				);

				this.close();
			}

			this.reset();

			const title = intl.formatMessage({
				id: 'socket.disconnect.title',
				defaultMessage: 'Lost Network Connection'
			});
			const description = intl.formatMessage({
				id: 'socket.disconnect.description',
				defaultMessage: 'Your network connection was interrupted.\nPlease click "Rejoin" to reconnect'
			});
			const okTitle = intl.formatMessage({
				id: 'label.reload',
				defaultMessage: 'Rejoin'
			});

			setTimeout(() =>
			{
				console.log('Lost media server connection!');
				store.dispatch(alertActions.setAlert({ title,
					description,
					okTitle,
					type: 'error',
					disableClose: true,
					onOk: () => window.location.reload()
				}));
			}, 3000);
		});

		this.signalingSocket.on('request', async (request, cb) =>
		{
			logger.debug(
				'socket "request" event [method:"%s", data:"%o"]',
				request.method,
				request.data
			);

			switch (request.method)
			{
				case 'newConsumer': {
					const {
						peerId,
						producerId,
						id,
						kind,
						rtpParameters,
						type,
						appData,
						producerPaused
					} = request.data;

					const consumer = await this.recvTransport.consume({
						id,
						producerId,
						kind,
						rtpParameters,
						appData: { ...appData, peerId } // Trick.
					});

					// Store in the map.
					this.consumers.set(consumer.id, consumer);

					consumer.on('transportclose', () =>
					{
						this.consumers.delete(consumer.id);
					});

					store.dispatch(
						consumerActions.addConsumer(
							{
								id: consumer.id,
								peerId: peerId,
								kind: kind,
								type: type,
								locallyPaused: false,
								remotelyPaused: producerPaused,
								rtpParameters: consumer.rtpParameters,
								source: consumer.appData.source,
								priority: 1,
								codec: consumer.rtpParameters.codecs[0].mimeType.split('/')[1],
								track: consumer.track
							},
							peerId
						)
					);

					if (consumer.appData.source === 'screen')
					{
						store.dispatch(roomActions.toggleFullscreen({ consumerId: consumer.id }));
					}

					// We are ready. Answer the request so the server will
					// resume this Consumer (which was paused for now).
					cb(null);

					if (kind === 'audio')
					{
						consumer.volume = 0;

						const stream = new MediaStream();

						stream.addTrack(consumer.track);

						if (!stream.getAudioTracks()[0])
							throw new Error(
								'request.newConsumer | given stream has no audio track'
							);

						consumer.hark = hark(stream, { play: false });

						consumer.hark.on('volumechange', (volume) =>
						{
							volume = Math.round(volume);

							if (consumer && volume !== consumer.volume)
							{
								consumer.volume = volume;

								store.dispatch(peerVolumeActions.setPeerVolume(peerId, volume));
							}
						});
					}

					break;
				}

				default: {
					logger.error('unknown request.method "%s"', request.method);

					cb(500, `unknown request.method "${request.method}"`);
				}
			}
		});

		this.signalingSocket.on('notification', async (notification) =>
		{
			logger.debug(
				'socket "notification" event [method:"%s", data:"%o"]',
				notification.method,
				notification.data
			);

			try
			{
				switch (notification.method)
				{
					case 'enteredLobby': {
						store.dispatch(roomActions.setInLobby(true));

						const { picture } = store.getState().me;

						await this.sendRequest('changeDisplayName', { displayName });
						await this.sendRequest('changePicture', { picture });
						break;
					}

					case 'overRoomLimit': {
						store.dispatch(roomActions.setOverRoomLimit(true));

						break;
					}

					case 'roomReady': {
						const { turnServers } = notification.data;

						this.turnServers = turnServers;

						store.dispatch(roomActions.toggleJoined());
						store.dispatch(roomActions.setInLobby(false));

						await this.joinRoom({ userId, type, displayName, joinVideo, joinAudio });

						break;
					}

					case 'parkedPeer': {
						const { peerId } = notification.data;

						store.dispatch(lobbyPeerActions.addLobbyPeer(peerId));
						toast(intl.formatMessage({
							id: 'room.newLobbyPeer',
							defaultMessage: 'New attendee entered the lobby'
						})
						);

						break;
					}

					case 'parkedPeers': {
						const { lobbyPeers } = notification.data;

						if (lobbyPeers.length > 0)
						{
							lobbyPeers.forEach((peer) =>
							{
								store.dispatch(lobbyPeerActions.addLobbyPeer(peer.id));
								store.dispatch(
									lobbyPeerActions.setLobbyPeerDisplayName(
										peer.displayName,
										peer.id
									)
								);
								store.dispatch(
									lobbyPeerActions.setLobbyPeerPicture(peer.picture, peer.id)
								);
							});

							toast(intl.formatMessage({
								id: 'room.newLobbyPeer',
								defaultMessage: 'New attendee entered the lobby'
							})
							);
						}

						break;
					}

					case 'lobby:peerClosed': {
						const { peerId } = notification.data;

						store.dispatch(lobbyPeerActions.removeLobbyPeer(peerId));

						toast(intl.formatMessage({
							id: 'room.lobbyPeerLeft',
							defaultMessage: 'Attendees in lobby left'
						})
						);

						break;
					}

					case 'lobby:promotedPeer': {
						const { peerId } = notification.data;

						store.dispatch(lobbyPeerActions.removeLobbyPeer(peerId));

						break;
					}

					case 'lobby:changeDisplayName': {
						const { peerId, displayName } = notification.data;

						store.dispatch(
							lobbyPeerActions.setLobbyPeerDisplayName(displayName, peerId)
						);

						toast(intl.formatMessage(
							{
								id: 'room.lobbyPeerChangedDisplayName',
								defaultMessage:
                      'Attendee in lobby changed name to {displayName}'
							},
							{
								displayName
							}
						)
						);

						break;
					}

					case 'lobby:changePicture': {
						const { peerId, picture } = notification.data;

						store.dispatch(
							lobbyPeerActions.setLobbyPeerPicture(picture, peerId)
						);

						toast(intl.formatMessage({
							id: 'room.lobbyPeerChangedPicture',
							defaultMessage: 'Attendee in lobby changed picture'
						})
						);

						break;
					}

					case 'setAccessCode': {
						const { accessCode } = notification.data;

						store.dispatch(roomActions.setAccessCode(accessCode));

						toast(intl.formatMessage({
							id: 'room.setAccessCode',
							defaultMessage: 'Access code for room updated'
						})
						);

						break;
					}

					case 'activeSpeaker': {
						const { peerId } = notification.data;

						store.dispatch(roomActions.setRoomActiveSpeaker(peerId));

						if (peerId && peerId !== this.peerId)
							this.spotlights.handleActiveSpeaker(peerId);

						break;
					}

					case 'closedCaption': {
						const { sttId, sessionId, offset, isFinal, text, peerId } = notification.data;

						store.dispatch(
							closedCaptionActions.addClosedCaption(sttId, sessionId, offset, isFinal, text, peerId)
						);

						break;
					}

					case 'activeCC': {
						const { closedCaption } = notification.data;

						store.dispatch(
							settingsActions.toggleClosedCaption(closedCaption)
						);

						if (!closedCaption)
						{
							store.dispatch(closedCaptionActions.clearClosedCaption());
						}

						break;
					}

					case 'moderator:kick': {
						this.close(true);
						store.dispatch(roomActions.setRoomKicked());

						break;
					}

					case 'sendFile':
					{
						const { roomFile } = notification.data;

						store.dispatch(fileActions.addFile(roomFile));
						toast(intl.formatMessage({
							id: 'room.newFile',
							defaultMessage: 'New file available'
						}));

						break;
					}

					case 'deleteFile':
					{
						const { id } = notification.data;

						store.dispatch(fileActions.deleteFile(id));
						toast(intl.formatMessage({
							id: 'room.deleteFile',
							defaultMessage: 'File was deleted'
						}));

						break;
					}

					case 'producerScore': {
						const { producerId, score } = notification.data;

						store.dispatch(producerActions.setProducerScore(producerId, score));

						break;
					}

					case 'newPeer': {
						const { id, displayName, userId, type, picture, roles } = notification.data;
						const peers = Object.values(store.getState().peers);
						const existingPeer = peers.find((peer) => peer.userId === userId);

						if (existingPeer)
						{
							store.dispatch(peerActions.removePeer(existingPeer.id));
						}

						store.dispatch(
							peerActions.addPeer({
								id,
								displayName,
								userId,
								picture,
								roles,
								consumers: [],
								type
							})
						);
						store.dispatch(roomActions.removeUsersFromWaiting(userId));

						if (type === 'stealth' || type === 'recording-bot')
						{
							break;
						}

						this.spotlights.newPeer(id);

						toast(intl.formatMessage(
							{
								id: 'room.newPeer',
								defaultMessage: '{displayName} joined the room'
							},
							{
								displayName
							}
						)
						);

						break;
					}

					case 'peerClosed': {
						const { peerId } = notification.data;
						const room = store.getState().room;
						const peers = Object.values(store.getState().peers);
						const user = store.getState().auth.user;
						const isRecordingPeer = peers.some((el) => el.id === peerId && el.recording);

						if (isRecordingPeer)
						{
							store.dispatch(roomActions.setRoomRecordingStatus(false));
						}

						this.spotlights.closePeer(peerId);
						store.dispatch(peerActions.removePeer(peerId));

						const remainPeers = Object.values(store.getState().peers);

						if (remainPeers.length === 0 && user?.type === 'recording-bot' && room)
						{
							await stopRecording(room.id);
						}

						break;
					}

					case 'consumerClosed': {
						const { consumerId } = notification.data;
						const consumer = this.consumers.get(consumerId);

						if (!consumer) break;

						consumer.close();

						if (consumer.hark != null) consumer.hark.stop();

						this.consumers.delete(consumerId);

						const { peerId } = consumer.appData;

						store.dispatch(consumerActions.removeConsumer(consumerId, peerId));

						break;
					}

					case 'consumerPaused': {
						const { consumerId } = notification.data;
						const consumer = this.consumers.get(consumerId);

						if (!consumer) break;

						store.dispatch(
							consumerActions.setConsumerPaused(consumerId, 'remote')
						);

						break;
					}

					case 'consumerResumed': {
						const { consumerId } = notification.data;
						const consumer = this.consumers.get(consumerId);

						if (!consumer) break;

						store.dispatch(
							consumerActions.setConsumerResumed(consumerId, 'remote')
						);

						break;
					}

					case 'moderator:mute': {
						this.muteMic();

						toast(intl.formatMessage({
							id: 'moderator.muteAudio',
							defaultMessage: 'Your audio has been muted by the Host'
						})
						);

						break;
					}

					case 'moderator:unmute': {
						this.unmuteMic();

						toast(intl.formatMessage({
							id: 'moderator.unmuteAudio',
							defaultMessage: 'The Host has unmuted your audio'
						})
						);

						break;
					}

					case 'moderator:stopVideo': {
						this.disableWebcam();

						toast(intl.formatMessage({
							id: 'moderator.muteVideo',
							defaultMessage: 'The Host has stopped your video'
						})
						);

						break;
					}

					case 'gotRole': {
						const { peerId, roleId } = notification.data;

						if (peerId === this.peerId)
						{
							store.dispatch(meActions.addRole(roleId));
						}
						else store.dispatch(peerActions.addPeerRole(peerId, roleId));

						break;
					}

					case 'lostRole': {
						const { peerId, roleId } = notification.data;

						if (peerId === this.peerId)
						{
							store.dispatch(meActions.removeRole(roleId));
						}
						else store.dispatch(peerActions.removePeerRole(peerId, roleId));

						break;
					}

					default: {
						logger.error(
							'unknown notification.method "%s"',
							notification.method
						);
					}
				}
			}
			catch (error)
			{
				logger.error(
					'error on socket "notification" event [error:"%o"]',
					error
				);

				toast(intl.formatMessage({
					id: 'socket.requestError',
					defaultMessage: 'Error on server request'
				})
				);
			}
		});
	}

	async joinRoom({ userId, type, displayName, joinVideo, joinAudio })
	{
		logger.debug('joinRoom()');

		const picture = store.getState().me.picture;

		try
		{
			this.mediasoupDevice = new mediasoupClient.Device();

			const routerRtpCapabilities = await this.sendRequest(
				'getRouterRtpCapabilities'
			);

			routerRtpCapabilities.headerExtensions =
        routerRtpCapabilities.headerExtensions.filter(
        	(ext) => ext.uri !== 'urn:3gpp:video-orientation'
        );

			await this.mediasoupDevice.load({ routerRtpCapabilities });
			await this.createSendTransport();
			await this.createRecvTransport();

			// Set our media capabilities.
			store.dispatch(
				meActions.setMediaCapabilities({
					canSendMic: type === 'stealth' ? false : this.mediasoupDevice.canProduce('audio'),
					canSendWebcam: type === 'stealth' ? false : this.mediasoupDevice.canProduce('video'),
					canShareScreen: type === 'stealth' ? false :
						this.mediasoupDevice.canProduce('video') &&
            this.screenSharing.isScreenShareAvailable(),
					canShareFiles: type === 'stealth' ? false : true
				})
			);

			const {
				authenticated,
				roles,
				peers,
				tracker,
				roomPermissions,
				userRoles,
				allowWhenRoleMissing,
				lastNHistory,
				lobbyPeers
			} = await this.sendRequest('join', {
				userId,
				type,
				displayName,
				picture,
				rtpCapabilities: this.mediasoupDevice.rtpCapabilities
			});

			logger.debug(
				'joinRoom() joined [authenticated:"%s", peers:"%o", roles:"%o", userRoles:"%o"]',
				authenticated,
				peers,
				roles,
				userRoles
			);

			tracker && (this.tracker = tracker);
			store.dispatch(roomActions.setRoomPermissions(roomPermissions));

			const roomUserRoles = new Map();

			Object.values(userRoles).forEach((val) => roomUserRoles.set(val.id, val));

			store.dispatch(roomActions.setUserRoles(roomUserRoles));

			if (allowWhenRoleMissing)
				store.dispatch(
					roomActions.setAllowWhenRoleMissing(allowWhenRoleMissing)
				);

			const myRoles = store.getState().me.roles;

			for (const roleId of roles)
			{
				if (!myRoles.some((myRoleId) => roleId === myRoleId))
				{
					store.dispatch(meActions.addRole(roleId));
				}
			}

			for (const peer of peers)
			{
				store.dispatch(peerActions.addPeer({ ...peer, consumers: [] }));
			}

			lobbyPeers.length > 0 &&
        lobbyPeers.forEach((peer) =>
        {
        	store.dispatch(lobbyPeerActions.addLobbyPeer(peer.id));
        	store.dispatch(
        		lobbyPeerActions.setLobbyPeerDisplayName(peer.displayName, peer.id)
        	);
        	store.dispatch(
        		lobbyPeerActions.setLobbyPeerPicture(peer.picture, peer.id)
        	);
        });

			// Don't produce if explicitly requested to not to do it.

			if (type !== 'stealth' && joinVideo && this.havePermission(permissions.SHARE_VIDEO))
			{
				this.updateWebcam({ init: true, start: true });
			}
			if (
				type !== 'stealth' &&
				joinAudio &&
          this.mediasoupDevice.canProduce('audio') &&
          this.havePermission(permissions.SHARE_AUDIO)
			)
			{
				await this.updateMic();
				await this.updateAudioOutputDevices();
			}

			const { selectedAudioOutputDevice } = store.getState().settings;

			if (!selectedAudioOutputDevice && this.audioOutputDevices)
			{
				store.dispatch(
					settingsActions.setSelectedAudioOutputDevice(
						Object.keys(this.audioOutputDevices)[0]
					)
				);
			}

			store.dispatch(roomActions.setRoomState('connected'));

			if (type !== 'stealth')
			{
				toast(intl.formatMessage({
					id: 'room.joined',
					defaultMessage: 'You have joined the room'
				})
				);
			}

			this.spotlights.addPeers(peers);

			if (lastNHistory.length > 0)
			{
				logger.debug('joinRoom() | got lastN history');

				this.spotlights.addSpeakerList(
					lastNHistory.filter((peerId) => peerId !== this.peerId)
				);
			}
		}
		catch (error)
		{
			logger.error('joinRoom() [error:"%o"]', error);

			toast(intl.formatMessage({
				id: 'room.cantJoin',
				defaultMessage: 'Unable to join the room'
			})
			);

			this.close();
			window.location.reload();
		}
	}

	async setAccessCode(code)
	{
		logger.debug('setAccessCode()');

		try
		{
			await this.sendRequest('setAccessCode', { accessCode: code });

			store.dispatch(roomActions.setAccessCode(code));

			toast('Access code saved.');
		}
		catch (error)
		{
			logger.error('setAccessCode() [error:"%o"]', error);
			toast('Unable to set access code.');
		}
	}

	async disableMic()
	{
		logger.debug('disableMic()');

		if (!this.micProducer) return;

		store.dispatch(meActions.setAudioInProgress(true));

		this.micProducer.close();

		store.dispatch(producerActions.removeProducer(this.micProducer.id));

		try
		{
			await this.sendRequest('closeProducer', {
				producerId: this.micProducer.id
			});
		}
		catch (error)
		{
			logger.error('disableMic() [error:"%o"]', error);
		}

		this.micProducer = null;

		store.dispatch(meActions.setAudioInProgress(false));
	}

	async updateScreenSharing({
		start = false,
		newResolution = null,
		newFrameRate = null
	} = {})
	{
		logger.debug('updateScreenSharing() [start:"%s"]', start);

		let track;

		try
		{
			const available = this.screenSharing.isScreenShareAvailable();

			if (!available) throw new Error('screen sharing not available');

			if (!this.mediasoupDevice.canProduce('video'))
				throw new Error('cannot produce video');

			if (newResolution)
				store.dispatch(
					settingsActions.setScreenSharingResolution(newResolution)
				);

			if (newFrameRate)
				store.dispatch(settingsActions.setScreenSharingFrameRate(newFrameRate));

			store.dispatch(meActions.setScreenShareInProgress(true));

			const { screenSharingResolution, screenSharingFrameRate } =
        store.getState().settings;

			if (start)
			{
				const stream = await this.screenSharing.start({
					...VIDEO_CONSTRAINS[screenSharingResolution],
					frameRate: screenSharingFrameRate
				});

				[ track ] = stream.getVideoTracks();

				if (this.useSharingSimulcast)
				{
					// If VP9 is the only available video codec then use SVC.
					const firstVideoCodec =
            this.mediasoupDevice.rtpCapabilities.codecs.find(
            	(c) => c.kind === 'video'
            );

					let encodings;

					if (firstVideoCodec.mimeType.toLowerCase() === 'video/vp9')
					{
						encodings = VIDEO_SVC_ENCODINGS;
					}
					else if ('simulcastEncodings' in window.config)
					{
						encodings = window.config.simulcastEncodings.map((encoding) => ({
							...encoding,
							dtx: true
						}));
					}
					else
					{
						encodings = VIDEO_SIMULCAST_ENCODINGS.map((encoding) => ({
							...encoding,
							dtx: true
						}));
					}

					this.screenSharingProducer = await this.sendTransport.produce({
						track,
						encodings,
						codecOptions: {
							videoGoogleStartBitrate: 1000
						},
						appData: {
							source: 'screen'
						}
					});
				}
				else
				{
					this.screenSharingProducer = await this.sendTransport.produce({
						track,
						appData: {
							source: 'screen'
						}
					});
				}

				store.dispatch(
					producerActions.addProducer({
						id: this.screenSharingProducer.id,
						deviceLabel: 'screen',
						source: 'screen',
						paused: this.screenSharingProducer.paused,
						track: this.screenSharingProducer.track,
						rtpParameters: this.screenSharingProducer.rtpParameters,
						codec:
              this.screenSharingProducer.rtpParameters.codecs[0].mimeType.split(
              	'/'
              )[1]
					})
				);
				store.dispatch(roomActions.toggleFullscreen({ producerId: this.screenSharingProducer.id }));

				this.screenSharingProducer.on('transportclose', () =>
				{
					this.screenSharingProducer = null;
				});

				this.screenSharingProducer.on('trackended', () =>
				{
					toast(intl.formatMessage({
						id: 'devices.screenSharingDisconnected',
						defaultMessage: 'Screen sharing disconnected'
					})
					);

					this.disableScreenSharing();
				});
			}
			else if (this.screenSharingProducer)
			{
				({ track } = this.screenSharingProducer);

				await track.applyConstraints({
					...VIDEO_CONSTRAINS[screenSharingResolution],
					frameRate: screenSharingFrameRate
				});
			}
		}
		catch (error)
		{
			logger.error('updateScreenSharing() [error:"%o"]', error);

			toast(intl.formatMessage({
				id: 'devices.screenSharingError',
				defaultMessage: 'An error occurred while accessing your screen'
			})
			);

			if (track) track.stop();
		}

		store.dispatch(meActions.setScreenShareInProgress(false));
	}

	async disableScreenSharing()
	{
		logger.debug('disableScreenSharing()');

		if (!this.screenSharingProducer) return;

		store.dispatch(meActions.setScreenShareInProgress(true));

		this.screenSharingProducer.close();

		store.dispatch(
			producerActions.removeProducer(this.screenSharingProducer.id)
		);

		try
		{
			await this.sendRequest('closeProducer', {
				producerId: this.screenSharingProducer.id
			});
		}
		catch (error)
		{
			logger.error('disableScreenSharing() [error:"%o"]', error);
		}

		this.screenSharingProducer = null;

		this.screenSharing.stop();

		store.dispatch(meActions.setScreenShareInProgress(false));
	}

	async disableWebcam()
	{
		logger.debug('disableWebcam()');

		if (!this.webcamProducer) return;

		store.dispatch(meActions.setWebcamInProgress(true));

		this.webcamProducer.close();

		store.dispatch(producerActions.removeProducer(this.webcamProducer.id));

		try
		{
			await this.sendRequest('closeProducer', {
				producerId: this.webcamProducer.id
			});
		}
		catch (error)
		{
			logger.error('disableWebcam() [error:"%o"]', error);
		}

		this.webcamProducer = null;
		store.dispatch(settingsActions.setVideoMuted(true));
		store.dispatch(meActions.setWebcamInProgress(false));
	}

	async setNoiseThreshold(threshold)
	{
		logger.debug('setNoiseThreshold() [threshold:"%s"]', threshold);

		this.hark.setThreshold(threshold);

		store.dispatch(settingsActions.setNoiseThreshold(threshold));
	}

	async updateAudioDevices()
	{
		logger.debug('updateAudioDevices()');

		// Reset the list.
		this.audioInputDevices = {};

		try
		{
			logger.debug('updateAudioDevices() | calling enumerateDevices()');

			this.audioInputDevices = await getDevicesByType('audioinput');

			store.dispatch(meActions.setAudioInputDevices(this.audioInputDevices));
		}
		catch (error)
		{
			logger.error('updateAudioDevices() [error:"%o"]', error);
		}
	}

	async updateWebcams()
	{
		logger.debug('updateWebcams()');

		// Reset the list.
		this.webcams = {};

		try
		{
			logger.debug('updateWebcams() | calling enumerateDevices()');

			this.webcams = await getDevicesByType('videoinput');

			store.dispatch(meActions.setWebcamDevices(this.webcams));
		}
		catch (error)
		{
			logger.error('updateWebcams() [error:"%o"]', error);
		}
	}

	async getAudioDeviceId()
	{
		logger.debug('getAudioDeviceId()');

		try
		{
			logger.debug('getAudioDeviceId() | calling updateAudioDeviceId()');

			await this.updateAudioDevices();

			const { selectedAudioDevice } = store.getState().settings;

			if (selectedAudioDevice && this.audioInputDevices[selectedAudioDevice])
				return selectedAudioDevice;
			else
			{
				const audioInputDevices = Object.values(this.audioInputDevices);

				return audioInputDevices[0] ? audioInputDevices[0].deviceId : null;
			}
		}
		catch (error)
		{
			logger.error('getAudioDeviceId() [error:"%o"]', error);
		}
	}

	async getWebcamDeviceId()
	{
		logger.debug('getWebcamDeviceId()');

		try
		{
			logger.debug('getWebcamDeviceId() | calling updateWebcams()');

			await this.updateWebcams();

			const { selectedWebcam } = store.getState().settings;

			if (selectedWebcam && this.webcams[selectedWebcam])
				return selectedWebcam;
			else
			{
				const webcams = Object.values(this.webcams);

				return webcams[0] ? webcams[0].deviceId : null;
			}
		}
		catch (error)
		{
			logger.error('getWebcamDeviceId() [error:"%o"]', error);
		}
	}

	async updateAudioOutputDevices()
	{
		logger.debug('updateAudioOutputDevices()');

		// Reset the list.
		this.audioOutputDevices = {};

		try
		{
			logger.debug('updateAudioOutputDevices() | calling enumerateDevices()');

			this.audioOutputDevices = await getDevicesByType('audiooutput');

			store.dispatch(meActions.setAudioOutputDevices(this.audioOutputDevices));
		}
		catch (error)
		{
			logger.error('updateAudioOutputDevices() [error:"%o"]', error);
		}
	}

	havePermission(permission)
	{
		const { roomPermissions, allowWhenRoleMissing } = store.getState().room;

		if (!roomPermissions) return false;

		const { roles } = store.getState().me;

		const permitted = roles.some((userRoleId) =>
			roomPermissions[permission].some(
				(permissionRole) => userRoleId === permissionRole.id
			)
		);

		if (permitted) return true;

		if (!allowWhenRoleMissing) return false;

		const peers = Object.values(store.getState().peers);

		// Allow if config is set, and no one is present
		if (
			allowWhenRoleMissing.includes(permission) &&
      peers.filter((peer) =>
      	peer.roles.some((roleId) =>
      		roomPermissions[permission].some(
      			(permissionRole) => roleId === permissionRole.id
      		)
      	)
      ).length === 0
		)
			return true;

		return false;
	}

	async createSendTransport()
	{

		const transportInfo = await this.sendRequest('createWebRtcTransport', {
			forceTcp: this.forceTcp,
			producing: true,
			consuming: false
		});

		const { id, iceParameters, iceCandidates, dtlsParameters } =
          transportInfo;

		this.sendTransport = this.mediasoupDevice.createSendTransport({
			id,
			iceParameters,
			iceCandidates,
			dtlsParameters,
			iceServers: this.turnServers,
			// TODO: Fix for issue #72
			iceTransportPolicy:
            this.device.flag === 'firefox' && this.turnServers
            	? 'relay'
            	: undefined,
			proprietaryConstraints: PC_PROPRIETARY_CONSTRAINTS
		});

		this.sendTransport.on(
			'connect',
			(
				{ dtlsParameters },
				callback,
				errback // eslint-disable-line no-shadow
			) =>
			{
				this.sendRequest('connectWebRtcTransport', {
					transportId: this.sendTransport.id,
					dtlsParameters
				})
					.then(callback)
					.catch(errback);
			}
		);

		this.sendTransport.on(
			'produce',
			async ({ kind, rtpParameters, appData }, callback, errback) =>
			{
				try
				{
					// eslint-disable-next-line no-shadow
					const { id } = await this.sendRequest('produce', {
						transportId: this.sendTransport.id,
						kind,
						rtpParameters,
						appData
					});

					callback({ id });
				}
				catch (error)
				{
					errback(error);
				}
			}
		);
	}

	async createRecvTransport()
	{
		const transportInfo = await this.sendRequest(
			'createWebRtcTransport',
			{
				forceTcp: this._forceTcp,
				producing: false,
				consuming: true
			});

		const {
			id,
			iceParameters,
			iceCandidates,
			dtlsParameters
		} = transportInfo;

		this.recvTransport = this.mediasoupDevice.createRecvTransport(
			{
				id,
				iceParameters,
				iceCandidates,
				dtlsParameters,
				iceServers: this.turnServers,
				// TODO: Fix for issue #72
				iceTransportPolicy: this.device.flag === 'firefox' && this.turnServers ? 'relay' : undefined
			});

		this.recvTransport.on(
			'connect', ({ dtlsParameters }, callback, errback) => // eslint-disable-line no-shadow
			{
				this.sendRequest(
					'connectWebRtcTransport',
					{
						transportId: this.recvTransport.id,
						dtlsParameters
					})
					.then(callback)
					.catch(errback);
			});
	}
}